import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

export default function PlatformAdmin() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [jingles, setJingles] = useState({ summary: {}, requests: [] });
  const [listeners, setListeners] = useState({ total_listeners: 0, companies: [] });
  const [broadcast, setBroadcast] = useState(null);
  const [licenses, setLicenses] = useState({ summary: {}, companies: [] });
  const [growth, setGrowth] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsage, setCompanyUsage] = useState(null);

  // Filters
  const [companySearch, setCompanySearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [jingleFilter, setJingleFilter] = useState('');
  const [listenerSearch, setListenerSearch] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const api = useCallback(async (endpoint, options = {}) => {
    try {
      const res = await fetch(`${API}${endpoint}`, { headers, ...options });
      if (res.status === 401) { navigate('/login'); return null; }
      if (res.status === 403) { setError('Platform admin access required'); return null; }
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error(`API ${endpoint}:`, err);
      return null;
    }
  }, [token, navigate]);

  const loadOverview = useCallback(async () => {
    const [ov, gr] = await Promise.all([
      api('/platform-admin/overview'),
      api('/platform-admin/stats/growth'),
    ]);
    if (ov) setOverview(ov);
    if (gr) setGrowth(gr);
  }, [api]);

  const loadCompanies = useCallback(async () => {
    let url = '/platform-admin/companies?';
    if (companySearch) url += `search=${encodeURIComponent(companySearch)}&`;
    if (tierFilter) url += `tier=${tierFilter}&`;
    const data = await api(url);
    if (data) setCompanies(data.companies || []);
  }, [api, companySearch, tierFilter]);

  const loadUsers = useCallback(async () => {
    const data = await api('/platform-admin/users');
    if (data) setUsers(data.users || []);
  }, [api]);

  const loadJingles = useCallback(async () => {
    let url = '/platform-admin/jingle-requests';
    if (jingleFilter) url += `?status=${jingleFilter}`;
    const data = await api(url);
    if (data) setJingles(data);
  }, [api, jingleFilter]);

  const loadListeners = useCallback(async () => {
    const data = await api('/platform-admin/listeners');
    if (data) setListeners(data);
  }, [api]);

  const loadLicenses = useCallback(async () => {
    const data = await api('/licenses/admin/license-overview');
    if (data) setLicenses(data);
  }, [api]);

  const loadBroadcast = useCallback(async () => {
    const data = await api('/broadcast/status');
    if (data) setBroadcast(data);
  }, [api]);

  const loadCompanyUsage = useCallback(async (companyId) => {
    const data = await api(`/platform-admin/companies/${companyId}/usage`);
    if (data) setCompanyUsage(data);
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      if (tab === 'overview') await loadOverview();
      else if (tab === 'companies') await loadCompanies();
      else if (tab === 'users') await loadUsers();
      else if (tab === 'jingles') await loadJingles();
      else if (tab === 'listeners') await loadListeners();
      else if (tab === 'licenses') await loadLicenses();
      else if (tab === 'broadcast') await loadBroadcast();
      setLoading(false);
    };
    load();
  }, [tab, loadOverview, loadCompanies, loadUsers, loadJingles, loadListeners, loadLicenses, loadBroadcast]);

  // Actions
  const updateSubscription = async (companyId, tier) => {
    if (!window.confirm(`Change subscription to ${tier}?`)) return;
    await api(`/platform-admin/companies/${companyId}/subscription?tier=${tier}`, { method: 'PUT' });
    loadCompanies();
    if (companyUsage && companyUsage.company.id === companyId) loadCompanyUsage(companyId);
  };

  const toggleCompanyStatus = async (companyId, currentActive) => {
    const action = currentActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${action} this company?`)) return;
    await api(`/platform-admin/companies/${companyId}/status?is_active=${!currentActive}`, { method: 'PUT' });
    loadCompanies();
  };

  const claimJingle = async (jingleId) => {
    await api(`/platform-admin/jingle-requests/${jingleId}/claim`, { method: 'PUT' });
    loadJingles();
  };

  const uploadJingle = async (jingleId) => {
    const audioUrl = prompt('Enter the audio URL for the completed jingle:');
    if (!audioUrl) return;
    const notes = prompt('Admin notes (optional):') || '';
    await api(`/platform-admin/jingle-requests/${jingleId}/upload`, {
      method: 'PUT',
      body: JSON.stringify({ audio_url: audioUrl, duration: 30, admin_notes: notes }),
    });
    loadJingles();
  };

  const restartScheduler = async () => {
    if (!window.confirm('Restart broadcast scheduler?')) return;
    await api('/broadcast/restart', { method: 'POST' });
    setTimeout(loadBroadcast, 2000);
  };

  const formatDate = (iso) => {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (iso) => {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString();
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'companies', label: 'Companies' },
    { id: 'listeners', label: 'Listeners' },
    { id: 'users', label: 'Users' },
    { id: 'jingles', label: 'Jingles' },
    { id: 'licenses', label: 'Licenses' },
    { id: 'broadcast', label: 'Broadcast' },
  ];

  const tierColors = {
    starter: 'bg-gray-100 text-gray-700',
    pro: 'bg-blue-100 text-blue-700',
    enterprise: 'bg-purple-100 text-purple-700',
  };

  const statusColors = {
    requested: 'bg-yellow-100 text-yellow-700',
    in_progress: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
          <p className="text-red-700 font-semibold text-lg mb-2">Access Denied</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Platform Administration</h1>
        <p className="text-sm text-gray-500 mt-1">Groovie superadmin dashboard</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setSelectedCompany(null); setCompanyUsage(null); }}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-xl"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
          </div>
        </div>
      ) : (
        <>
          {/* ═══ OVERVIEW TAB ═══ */}
          {tab === 'overview' && overview && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Companies', value: overview.metrics.total_companies },
                  { label: 'Users', value: overview.metrics.total_users },
                  { label: 'Locations', value: overview.metrics.total_locations },
                  { label: 'Channels', value: overview.metrics.total_channels },
                  { label: 'Public Channels', value: overview.metrics.public_channels },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* MRR Card */}
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-6 text-white">
                  <p className="text-purple-200 text-sm">Monthly Recurring Revenue</p>
                  <p className="text-4xl font-bold mt-2">${overview.mrr?.toLocaleString() || 0}</p>
                  <div className="mt-4 flex gap-4 text-sm">
                    <span className="bg-white/20 px-3 py-1 rounded-full">Starter: {overview.subscriptions.starter}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full">Pro: {overview.subscriptions.pro}</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full">Enterprise: {overview.subscriptions.enterprise}</span>
                  </div>
                </div>

                {/* Growth Card */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <p className="font-semibold text-gray-900 mb-3">Recent Signups (30 days)</p>
                  <p className="text-3xl font-bold text-green-600 mb-4">{growth?.total_last_30_days || 0}</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {growth?.recent_signups?.slice(0, 8).map(c => (
                      <div key={c.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{c.name}</span>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${tierColors[c.subscription_tier] || 'bg-gray-100'}`}>{c.subscription_tier}</span>
                          <span className="text-gray-400 text-xs">{formatDate(c.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ═══ COMPANIES TAB ═══ */}
          {tab === 'companies' && (
            <div className="space-y-4">
              {/* Company Detail Panel */}
              {companyUsage && (
                <div className="bg-white rounded-xl border-2 border-purple-300 p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{companyUsage.company.name}</h3>
                      <p className="text-sm text-gray-500">{companyUsage.company.email} &middot; {companyUsage.company.business_type} &middot; {companyUsage.company.industry}</p>
                    </div>
                    <button onClick={() => { setCompanyUsage(null); setSelectedCompany(null); }}
                      className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                    {Object.entries(companyUsage.usage).map(([key, val]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-gray-900">{val}</div>
                        <div className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-purple-700">{companyUsage.stats.total_listeners}</div>
                      <div className="text-xs text-purple-600">Listeners</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-blue-700">{companyUsage.stats.total_plays}</div>
                      <div className="text-xs text-blue-600">Total Plays</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-green-700">{companyUsage.stats.total_ad_plays}</div>
                      <div className="text-xs text-green-600">Ad Plays</div>
                    </div>
                  </div>
                  {companyUsage.channels.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Channels</p>
                      <div className="space-y-2">
                        {companyUsage.channels.map(ch => (
                          <div key={ch.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div>
                              <span className="font-medium text-sm">{ch.name}</span>
                              {ch.is_public && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Public</span>}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>{ch.listener_count} listeners</span>
                              <span>{ch.total_plays} plays</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="flex gap-3 flex-wrap">
                <input type="text" placeholder="Search companies..." value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadCompanies()}
                  className="border border-gray-300 rounded-lg px-4 py-2 text-sm flex-1 min-w-[200px] focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                <select value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setTimeout(loadCompanies, 100); }}
                  className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                  <option value="">All Tiers</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <button onClick={loadCompanies} className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700">Search</button>
              </div>

              {/* Companies Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Company</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Type</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Tier</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Locations</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Channels</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Joined</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {companies.map(c => (
                      <tr key={c.id} className={`hover:bg-gray-50 ${selectedCompany === c.id ? 'bg-purple-50' : ''}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => { setSelectedCompany(c.id); loadCompanyUsage(c.id); }}
                            className="text-sm font-medium text-purple-600 hover:text-purple-800 text-left">{c.name}</button>
                          <p className="text-xs text-gray-400">{c.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{c.business_type || '\u2014'}</td>
                        <td className="px-4 py-3">
                          <select value={c.subscription_tier} onChange={(e) => updateSubscription(c.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${tierColors[c.subscription_tier] || 'bg-gray-100'}`}>
                            <option value="starter">Starter</option>
                            <option value="pro">Pro</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">{c.locations_count}</td>
                        <td className="px-4 py-3 text-center text-sm">{c.channels_count}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(c.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => toggleCompanyStatus(c.id, c.subscription_status === 'active')}
                            className={`text-xs px-3 py-1 rounded-full ${c.subscription_status === 'active' ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                            {c.subscription_status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {companies.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No companies found</p>}
              </div>
            </div>
          )}

          {/* ═══ LISTENERS TAB ═══ */}
          {tab === 'listeners' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="bg-purple-600 text-white rounded-xl px-6 py-4">
                  <p className="text-purple-200 text-sm">Total Active Listeners</p>
                  <p className="text-3xl font-bold">{listeners.total_listeners}</p>
                </div>
                <div className="flex gap-3">
                  <input type="text" placeholder="Search by business..." value={listenerSearch}
                    onChange={(e) => setListenerSearch(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                  <button onClick={loadListeners} className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-sm">Refresh</button>
                </div>
              </div>

              <div className="space-y-3">
                {listeners.companies
                  .filter(c => !listenerSearch || c.company_name.toLowerCase().includes(listenerSearch.toLowerCase()))
                  .map(company => (
                  <div key={company.company_id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-semibold text-gray-900">{company.company_name}</span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${tierColors[company.subscription_tier] || ''}`}>{company.subscription_tier}</span>
                      </div>
                      <span className="text-lg font-bold text-purple-600">{company.total_listeners} listeners</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {company.channels.map(ch => (
                        <div key={ch.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${ch.listeners > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                            <span className="text-sm text-gray-700">{ch.name}</span>
                            {ch.is_public && <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">Public</span>}
                          </div>
                          <span className="text-sm font-medium">{ch.listeners}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {listeners.companies.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-8">No stations with listeners data</p>
                )}
              </div>
            </div>
          )}

          {/* ═══ USERS TAB ═══ */}
          {tab === 'users' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">User</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Role</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Company</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Last Login</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{u.name}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${u.is_platform_admin ? 'bg-purple-100 text-purple-700' : u.role === 'owner' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {u.is_platform_admin ? 'Platform Admin' : u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">ID: {u.company_id}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(u.last_login)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No users found</p>}
            </div>
          )}

          {/* ═══ JINGLES TAB ═══ */}
          {tab === 'jingles' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Pending', value: jingles.summary?.requested || 0, color: 'yellow' },
                  { label: 'In Progress', value: jingles.summary?.in_progress || 0, color: 'blue' },
                  { label: 'Complete', value: jingles.summary?.complete || 0, color: 'green' },
                  { label: 'Total', value: jingles.summary?.total || 0, color: 'gray' },
                ].map(s => (
                  <div key={s.label} className={`bg-${s.color}-50 rounded-xl border border-${s.color}-200 p-4 text-center`}>
                    <div className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</div>
                    <div className={`text-xs text-${s.color}-600 mt-1`}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                {['', 'requested', 'in_progress', 'complete'].map(f => (
                  <button key={f} onClick={() => { setJingleFilter(f); setTimeout(loadJingles, 100); }}
                    className={`px-3 py-1.5 rounded-lg text-sm ${jingleFilter === f ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {f || 'All'}
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                {jingles.requests?.map(j => (
                  <div key={j.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{j.name}</p>
                        <p className="text-sm text-gray-500">{j.company_name} &middot; {j.jingle_type} &middot; {j.style} &middot; {j.mood}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[j.request_status] || 'bg-gray-100'}`}>
                        {j.request_status?.replace('_', ' ')}
                      </span>
                    </div>
                    {j.description && <p className="text-sm text-gray-600 mb-2">{j.description}</p>}
                    {j.special_instructions && <p className="text-xs text-gray-400 italic mb-2">Instructions: {j.special_instructions}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Requested: {formatDate(j.requested_at)}</span>
                      <div className="flex gap-2">
                        {j.request_status === 'requested' && (
                          <button onClick={() => claimJingle(j.id)}
                            className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-blue-700">Claim</button>
                        )}
                        {(j.request_status === 'requested' || j.request_status === 'in_progress') && (
                          <button onClick={() => uploadJingle(j.id)}
                            className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-green-700">Upload Audio</button>
                        )}
                        {j.audio_url && (
                          <a href={j.audio_url} target="_blank" rel="noreferrer"
                            className="bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-200">Listen</a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(!jingles.requests || jingles.requests.length === 0) && (
                  <p className="text-gray-400 text-sm text-center py-8">No jingle requests</p>
                )}
              </div>
            </div>
          )}

          {/* ═══ LICENSES TAB ═══ */}
          {tab === 'licenses' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Companies', value: licenses.summary?.total_companies || 0, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
                  { label: 'Licensed', value: licenses.summary?.signed || 0, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
                  { label: 'Unsigned', value: licenses.summary?.unsigned || 0, bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
                  { label: 'With Violations', value: licenses.summary?.with_violations || 0, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
                ].map(s => (
                  <div key={s.label} className={`${s.bg} rounded-xl border ${s.border} p-4 text-center`}>
                    <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Company</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Tier</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">License</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Signed By</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Date</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Devices</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Locations</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-4 py-3">Violations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {licenses.companies?.map(c => (
                      <tr key={c.company_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{c.company_name}</p>
                          <p className="text-xs text-gray-400">{c.business_type || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${tierColors[c.subscription_tier] || 'bg-gray-100'}`}>{c.subscription_tier}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {c.has_license ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium">Not Signed</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {c.signed_by ? (
                            <div>
                              <p className="text-sm text-gray-700">{c.signed_by}</p>
                              <p className="text-xs text-gray-400">{c.signed_by_title}</p>
                            </div>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{c.signed_at ? formatDate(c.signed_at) : '—'}</td>
                        <td className="px-4 py-3 text-center text-sm">{c.active_devices}</td>
                        <td className="px-4 py-3 text-center text-sm">{c.total_locations}</td>
                        <td className="px-4 py-3 text-center">
                          {c.violations > 0 ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">{c.violations}</span>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!licenses.companies || licenses.companies.length === 0) && (
                  <p className="text-gray-400 text-sm text-center py-8">No companies found</p>
                )}
              </div>
            </div>
          )}

          {/* ═══ BROADCAST TAB ═══ */}
          {tab === 'broadcast' && broadcast && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl border-2 ${broadcast.running ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full ${broadcast.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <div>
                      <span className="font-semibold text-lg">{broadcast.running ? 'Engine Running' : 'Engine Stopped'}</span>
                      {broadcast.started_at && <p className="text-sm text-gray-600">Started: {formatTime(broadcast.started_at)}</p>}
                    </div>
                  </div>
                  <button onClick={restartScheduler} className="px-3 py-2 text-sm bg-red-50 text-red-600 hover:bg-red-100 rounded-lg">Restart</button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Programs', value: broadcast.stats?.programs_switched || 0 },
                  { label: 'Announcements', value: broadcast.stats?.announcements_played || 0 },
                  { label: 'Events', value: broadcast.stats?.events_triggered || 0 },
                  { label: 'Ads', value: broadcast.stats?.ads_played || 0 },
                  { label: 'News', value: broadcast.stats?.news_fetched || 0 },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold mb-4">Scheduled Jobs</h2>
                  <div className="space-y-3">
                    {broadcast.jobs?.map(job => (
                      <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-sm text-gray-900">{job.name}</p>
                        <p className="text-xs text-gray-400">{formatTime(job.next_run)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {broadcast.recent_errors?.length > 0 && (
                  <div className="bg-white rounded-xl border border-red-200 p-5">
                    <h2 className="text-lg font-semibold mb-4 text-red-700">Recent Errors</h2>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {broadcast.recent_errors.map((err, i) => (
                        <div key={i} className="p-2 bg-red-50 rounded text-sm">
                          <span className="text-xs text-gray-400">{formatTime(err.time)}</span>
                          <p className="text-red-700 text-xs mt-1">{err.message?.substring(0, 200)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}